import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faUsers, faQrcode, faUpRightFromSquare, faCircleInfo,
  faPlay, faCircleCheck, faChurch, faSpinner, faCopy, faFileLines, faTv, faEyeSlash,
  faMinus, faPlus, faUserMinus, faTriangleExclamation, faCircleQuestion, faXmark, faCalendarDay,
  faClock
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import io from 'socket.io-client';
import { useAuth } from '../context/AuthContext';
import TelaoView from '../components/TelaoView';
import {
  getElection, updateElection, updateElectionMinistry, addCandidate, deleteCandidate,
  openRound, closeRound, cancelRound, voidRound, resetMinistry, setElected,
  setMinistryStatus, applyLeader, rejectNominee, confirmNominees, featureMinistry,
  removeOneCheckin, deriveMinistry, deleteDerivedMinistry, updateDiscussion, newPresenceSession,
  nudgeRound
} from '../services/api';
import MinistryPanel from './ElectionMinistryPanel';
import './ElectionDashboard.css';

const ElectionDashboard = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, isSecretary } = useAuth();

  const [election, setElection] = useState(null);
  const [checkins, setCheckins] = useState(0);
  // Total de celulares que já abriram a votação hoje (inclui quem foi embora) — só referência
  const [seenTotal, setSeenTotal] = useState(0);
  const [liveVotes, setLiveVotes] = useState(null);
  const [selectedMid, setSelectedMid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastResult, setLastResult] = useState(null);
  const [publicState, setPublicState] = useState(null);
  const [savingVoters, setSavingVoters] = useState(false);
  const [votersInput, setVotersInput] = useState('');
  const [showDupHelp, setShowDupHelp] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removingCheckin, setRemovingCheckin] = useState(false);
  // Janela de presença (minutos): quem não deu sinal de vida nesse período deixa de contar
  const [windowInput, setWindowInput] = useState('');
  const [savingWindow, setSavingWindow] = useState(false);

  const socketRef = useRef(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await getElection(id);
      setElection(data.election);
      setCheckins(data.checkins);
      if (typeof data.seenTotal === 'number') setSeenTotal(data.seenTotal);
      setLiveVotes(data.liveVotes);
      if (data.publicState) setPublicState(data.publicState);
      return data.election;
    } catch (e) {
      toast.error('Erro ao carregar a assembleia.');
      return null;
    }
  }, [id]);

  useEffect(() => {
    if (!isAdmin && !isSecretary) {
      navigate('/');
      return;
    }
    (async () => {
      setLoading(true);
      const el = await load();
      setLoading(false);
      if (!el) return;

      const serverUrl = process.env.REACT_APP_API_URL || window.location.origin;
      socketRef.current = io(serverUrl, { transports: ['websocket', 'polling'], reconnection: true });
      socketRef.current.on('connect', () => socketRef.current.emit('election:join', el._id));
      socketRef.current.on('checkin:count', (d) => setCheckins(d.checkins));
      socketRef.current.on('vote:count', (d) => setLiveVotes(d));
      socketRef.current.on('round:opened', () => { setLastResult(null); load(); });
      socketRef.current.on('round:closed', (r) => { setLastResult(r); load(); });
      socketRef.current.on('round:cancelled', () => { setLastResult(null); setLiveVotes(null); load(); });
      socketRef.current.on('election:state', (s) => setPublicState(s));

      pollRef.current = setInterval(load, 5000);
    })();

    return () => {
      if (socketRef.current) socketRef.current.disconnect();
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [id, isAdmin, isSecretary, navigate, load]);

  const voteUrl = election ? `${window.location.origin}/votar?code=${election.publicCode}` : '';
  const telaoUrl = election ? `${window.location.origin}/telao?code=${election.publicCode}` : '';

  const votingSub = election?.ministries.find(m => m.status === 'voting');
  const featuredMid = election?.featuredMinistryId || null;
  const featuredSub = featuredMid ? election.ministries.find(m => m._id === featuredMid) : null;

  // Prévia = mesmo estado público que o telão recebe do servidor, com os
  // contadores ao vivo (presença/votos) sobrepostos para não esperar o polling.
  const previewState = publicState ? {
    ...publicState,
    checkins,
    expectedVoters: election?.expectedVoters,
    voting: publicState.voting ? {
      ...publicState.voting,
      votesCount: (liveVotes && liveVotes.electionMinistryId === publicState.voting.electionMinistryId)
        ? liveVotes.votesCount : publicState.voting.votesCount
    } : null
  } : election ? {
    title: election.title,
    publicCode: election.publicCode,
    checkins,
    expectedVoters: election.expectedVoters,
    rule: election.rule,
    voting: votingSub ? {
      electionMinistryId: votingSub._id,
      name: votingSub.name,
      round: votingSub.rounds.length,
      seats: votingSub.seats || 1,
      seatsOpen: (votingSub.rounds[votingSub.rounds.length - 1] || {}).seatsOpen || 1,
      votesPerVoter: (votingSub.rounds[votingSub.rounds.length - 1] || {}).votesPerVoter || 1,
      candidates: votingSub.candidates,
      votesCount: (liveVotes && liveVotes.electionMinistryId === votingSub._id) ? liveVotes.votesCount : 0
    } : null
  } : null;

  // Mantém o campo "Esperados" sincronizado com o servidor (sem atropelar a digitação)
  const expectedFromServer = election?.expectedVoters;
  useEffect(() => {
    setVotersInput(expectedFromServer ? String(expectedFromServer) : '');
  }, [expectedFromServer]);

  const windowFromServer = election?.presenceWindowMin || 30;
  useEffect(() => {
    setWindowInput(String(windowFromServer));
  }, [windowFromServer]);

  const handleSaveWindow = async (value) => {
    const n = parseInt(value, 10);
    if (isNaN(n)) { setWindowInput(String(windowFromServer)); return; }
    const next = Math.min(180, Math.max(5, n));
    if (next === windowFromServer) { setWindowInput(String(next)); return; }
    setSavingWindow(true);
    try {
      const { data } = await updateElection(id, { presenceWindowMin: next });
      setElection(data);
      await load();
      toast.success(`Janela de presença: ${next} min.`);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Não foi possível salvar a janela de presença.');
      setWindowInput(String(windowFromServer));
    } finally {
      setSavingWindow(false);
    }
  };

  const handleSaveVoters = async (value) => {
    const next = Math.max(0, parseInt(value, 10) || 0);
    if (next === (election?.expectedVoters || 0)) return;
    setSavingVoters(true);
    try {
      const { data } = await updateElection(id, { expectedVoters: next });
      setElection(data);
    } catch (e) {
      toast.error('Erro ao salvar.');
    } finally {
      setSavingVoters(false);
    }
  };

  const stepVoters = (delta) => {
    const next = Math.max(0, (parseInt(votersInput, 10) || 0) + delta);
    setVotersInput(next ? String(next) : '');
    handleSaveVoters(next);
  };

  const handleRemoveCheckin = async () => {
    setRemovingCheckin(true);
    try {
      const { data } = await removeOneCheckin(id);
      setCheckins(data.checkins);
      toast.success(`Presença removida. Agora: ${data.checkins} presente(s).`);
      setConfirmRemove(false);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Não foi possível remover a presença.');
    } finally {
      setRemovingCheckin(false);
    }
  };

  // Nova sessão de presença (novo dia da assembleia)
  const handleNewSession = async () => {
    const ok = window.confirm(
      'Iniciar nova sessão de presença?\n\n'
      + `Os ${checkins} presente(s) atuais serão zerados. Quem estiver com a página de votação aberta `
      + 'volta a contar sozinho em poucos segundos; quem não veio hoje deixa de contar.\n\n'
      + 'Rodadas, votos e eleitos NÃO são alterados.'
    );
    if (!ok) return;
    try {
      await newPresenceSession(id);
      setCheckins(0);
      await load();
      toast.success('Nova sessão iniciada. A presença está sendo contada de novo.');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Não foi possível iniciar nova sessão.');
    }
  };

  const runAction = async (fn, successMsg) => {
    try {
      await fn();
      await load();
      if (successMsg) toast.success(successMsg);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Não foi possível concluir a ação.');
    }
  };

  const actions = {
    addCandidate: (mid, data) => runAction(() => addCandidate(id, mid, data), 'Candidato adicionado.'),
    deleteCandidate: (mid, cid) => runAction(() => deleteCandidate(id, mid, cid), 'Candidato removido.'),
    openRound: (mid) => runAction(() => openRound(id, mid), 'Votação aberta!'),
    closeRound: (mid) => runAction(() => closeRound(id, mid), 'Votação encerrada.'),
    nudge: async (mid) => {
      try {
        const { data } = await nudgeRound(id, mid);
        const missing = data?.missing ?? 0;
        toast.success(missing > 0
          ? `Alerta enviado. ${missing} presente(s) ainda não votaram.`
          : 'Alerta enviado para quem ainda não votou.');
      } catch (e) {
        toast.error(e.response?.data?.message || 'Não foi possível enviar o alerta.');
      }
    },
    cancelRound: (mid) => runAction(() => cancelRound(id, mid), 'Votação cancelada. Os votos foram descartados.'),
    voidRound: (mid, round) => runAction(() => voidRound(id, mid, round), 'Rodada anulada.'),
    resetMinistry: (mid) => runAction(() => resetMinistry(id, mid), 'Ministério zerado.'),
    setElected: (mid, name) => runAction(() => setElected(id, mid, name), 'Eleito definido.'),
    setStatus: (mid, status) => runAction(() => setMinistryStatus(id, mid, status), 'Status atualizado.'),
    applyLeader: (mid) => runAction(() => applyLeader(id, mid), 'Líder atualizado no cadastro do ministério.'),
    updateMinistry: (mid, data) => runAction(() => updateElectionMinistry(id, mid, data), 'Vagas atualizadas.'),
    rejectNominee: (mid, round, cid, data) => runAction(() => rejectNominee(id, mid, round, cid, data)),
    confirmNominees: (mid) => runAction(() => confirmNominees(id, mid), 'Eleitos confirmados.'),
    feature: (mid, view = 'names') => runAction(
      () => featureMinistry(id, mid, view),
      view === 'result' ? 'Resultado exibido no telão.' : view === 'elected' ? 'Eleito(s) exibido(s) no telão.' : 'Nomes exibidos no telão.'
    ),
    clearFeature: () => runAction(() => featureMinistry(id, null), 'Telão voltou para a tela de espera.'),
    // Discussão de nomes: atualização otimista (marcar tem que ser instantâneo) e depois confirma no servidor
    discussion: async (mid, data) => {
      if (Array.isArray(data.updates)) {
        setElection(prev => prev && {
          ...prev,
          ministries: prev.ministries.map(m => (m._id !== mid ? m : {
            ...m,
            candidates: m.candidates.map(c => {
              const u = data.updates.find(x => x.cid === c._id);
              return u ? { ...c, discussion: u.state } : c;
            })
          }))
        });
      }
      try {
        await updateDiscussion(id, mid, data);
      } catch (e) {
        toast.error(e.response?.data?.message || 'Não foi possível atualizar a discussão.');
      }
      await load();
    },
    // Cria a etapa derivada e já seleciona ela no painel
    derive: async (mid, data) => {
      try {
        const { data: created } = await deriveMinistry(id, mid, data);
        await load();
        if (created && created._id) setSelectedMid(created._id);
        toast.success(`"${created.name}" criado. Confira os nomes e abra a votação quando estiver pronto.`);
        return true;
      } catch (e) {
        toast.error(e.response?.data?.message || 'Não foi possível criar a votação.');
        return false;
      }
    },
    removeDerived: async (mid, parentId) => {
      try {
        await deleteDerivedMinistry(id, mid);
        await load();
        setSelectedMid(parentId || null);
        toast.success('Etapa removida.');
      } catch (e) {
        toast.error(e.response?.data?.message || 'Não foi possível remover.');
      }
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.info('Copiado!');
  };

  if (loading) return <div className="loading">Carregando...</div>;
  if (!election) return null;

  const selected = election.ministries.find(m => m._id === selectedMid);

  // Possível contagem dupla: mais presentes (ou votos) do que o esperado
  const expected = election.expectedVoters || 0;
  const currentVotes = votingSub && liveVotes && liveVotes.electionMinistryId === votingSub._id
    ? liveVotes.votesCount : null;
  const overPresence = expected > 0 && checkins > expected;
  const overVotes = expected > 0 && currentVotes !== null && currentVotes > expected;
  const dupWarning = overPresence || overVotes;

  return (
    <div className="ed-page">
      <div className="ed-header">
        <button className="btn-back" onClick={() => navigate('/admin/votacoes')} title="Voltar para a lista de votações">
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <div className="ed-header-title">
          <h1>{election.title}</h1>
          <span className="ed-code" title="Código da sessão">Código: <strong>{election.publicCode}</strong></span>
        </div>
        <div
          className={`ed-header-presence ${dupWarning ? 'is-over' : ''}`}
          title={`Presentes agora / esperados. Conta quem deu sinal de vida nos últimos ${windowFromServer} min; quem foi embora ou fechou a página some sozinho.${seenTotal > checkins ? ` Hoje ${seenTotal} celular(es) abriram a votação.` : ''}`}
        >
          <FontAwesomeIcon icon={dupWarning ? faTriangleExclamation : faUsers} />
          <span className="ed-presence-num">{checkins}{election.expectedVoters ? ` / ${election.expectedVoters}` : ''}</span>
          <span className="ed-presence-label">
            presentes agora
            {seenTotal > checkins && <small>vistos hoje: {seenTotal}</small>}
          </span>
        </div>
      </div>

      {dupWarning && (
        <div className="ed-dup-alert">
          <FontAwesomeIcon icon={faTriangleExclamation} />
          <span>
            {overVotes
              ? <>Há <strong>{currentVotes} votos</strong> nesta rodada para <strong>{expected} esperados</strong>.</>
              : <>Há <strong>{checkins} presentes</strong> para <strong>{expected} esperados</strong>.</>}
            {' '}Provável contagem dupla (alguém abriu em dois navegadores) ou o número de esperados está desatualizado.
          </span>
          <button className="btn btn-sm btn-secondary" onClick={() => setShowDupHelp(true)}>
            <FontAwesomeIcon icon={faCircleQuestion} /> Ver cenários
          </button>
        </div>
      )}

      {showDupHelp && (
        <div className="ed-dup-help">
          <div className="ed-dup-help-head">
            <h3><FontAwesomeIcon icon={faCircleQuestion} /> Quando uma pessoa pode contar duas vezes?</h3>
            <button className="ed-dup-help-close" onClick={() => setShowDupHelp(false)} title="Fechar">
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
          <p className="ed-dup-help-intro">
            A votação é anônima: o sistema reconhece o <strong>navegador</strong>, não a pessoa.
            Cada navegador diferente conta como um presente novo — e pode votar de novo.
          </p>
          <ol className="ed-dup-help-list">
            <li><strong>Abriu pelo WhatsApp/Instagram e depois no Chrome/Safari.</strong> O link aberto dentro do app (navegador do WhatsApp) e o Chrome são navegadores diferentes. É o caso mais comum.</li>
            <li><strong>Dois navegadores no mesmo celular</strong> (ex.: Chrome e Samsung Internet), ou escaneou o QR com a câmera e depois com outro app.</li>
            <li><strong>Aba anônima/privada.</strong> Cada aba anônima é um navegador “novo”.</li>
            <li><strong>Limpou o histórico/dados do navegador</strong> no meio da assembleia.</li>
            <li><strong>Abriu no celular e no tablet/computador.</strong></li>
            <li><strong>Foi embora sem sair.</strong> Não é contagem dupla e <em>resolve-se sozinho</em>: quem não dá sinal de vida por {windowFromServer} min deixa de contar (ajuste a janela no campo <strong>min</strong>).</li>
            <li><strong>Assembleia em mais de um dia.</strong> Também se resolve sozinho pela janela de presença. “Nova sessão (novo dia)” continua disponível se quiser zerar tudo de uma vez.</li>
          </ol>
          <div className="ed-dup-help-todo">
            <strong>O que fazer:</strong>
            <ul>
              <li>Peça para a pessoa tocar em <strong>“Sair da assembleia”</strong> (no fim da tela de votação) no navegador que ela <strong>não</strong> vai usar. Em seguida, continuar só no outro. Se ela simplesmente fechar o navegador abandonado, ele deixa de contar sozinho após {windowFromServer} min.</li>
              <li>Se não der para falar com a pessoa (ou ela já foi embora), use <strong>“Remover 1 presença”</strong> aqui no painel.</li>
              <li>Ajuste <strong>“Esperados”</strong> com os botões − / + quando alguém chegar ou sair.</li>
              <li>Oriente no início: <em>“Abra o link uma vez só e continue no mesmo lugar até o fim.”</em></li>
            </ul>
            <p className="ed-dup-help-note">
              O resultado não “trava” por causa disso: a maioria é calculada sobre os votos registrados na rodada,
              e não sobre os presentes. O risco real é a mesma pessoa votar duas vezes; por isso fique de olho
              quando houver mais votos do que esperados.
            </p>
          </div>
        </div>
      )}

      <div className="ed-linkbar">
        <div className="ed-link-item">
          <FontAwesomeIcon icon={faQrcode} />
          <span className="ed-link-text">{voteUrl}</span>
          <button className="btn btn-sm btn-secondary" onClick={() => copy(voteUrl)} title="Copiar link de votação">
            <FontAwesomeIcon icon={faCopy} />
          </button>
        </div>
        <div className="ed-link-actions">
          <div className="ed-voters-field" title="Votantes esperados: ajuste quando alguém chegar ou for embora">
            <FontAwesomeIcon icon={faUsers} />
            <button type="button" className="ed-step-btn" onClick={() => stepVoters(-1)} title="Diminuir 1 esperado" disabled={savingVoters}>
              <FontAwesomeIcon icon={faMinus} />
            </button>
            <input
              type="number"
              min="0"
              value={votersInput}
              placeholder="Esperados"
              aria-label="Votantes esperados"
              onChange={(e) => setVotersInput(e.target.value)}
              onBlur={(e) => handleSaveVoters(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            />
            <button type="button" className="ed-step-btn" onClick={() => stepVoters(1)} title="Aumentar 1 esperado" disabled={savingVoters}>
              <FontAwesomeIcon icon={faPlus} />
            </button>
            {savingVoters && <FontAwesomeIcon icon={faSpinner} spin />}
          </div>
          <div
            className="ed-voters-field ed-window-field"
            title="Janela de presença (minutos): conta como presente quem deu sinal de vida nesse período. Janela maior = número mais estável; menor = quem foi embora some mais rápido. Entre 5 e 180."
          >
            <FontAwesomeIcon icon={faClock} />
            <input
              type="number"
              min="5"
              max="180"
              step="5"
              value={windowInput}
              aria-label="Janela de presença em minutos"
              onChange={(e) => setWindowInput(e.target.value)}
              onBlur={(e) => handleSaveWindow(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
              disabled={savingWindow}
            />
            <span className="ed-window-unit">min</span>
            {savingWindow && <FontAwesomeIcon icon={faSpinner} spin />}
          </div>
          {!confirmRemove ? (
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => setConfirmRemove(true)}
              disabled={checkins <= 0}
              title="Tira 1 da contagem de presentes (ex.: alguém foi embora ou abriu em dois navegadores)"
            >
              <FontAwesomeIcon icon={faUserMinus} /> Remover 1 presença
            </button>
          ) : (
            <span className="ed-remove-confirm">
              Remover 1 de {checkins}?
              <button className="btn btn-sm btn-danger-outline" onClick={handleRemoveCheckin} disabled={removingCheckin}>
                {removingCheckin ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Remover'}
              </button>
              <button className="btn btn-sm btn-secondary" onClick={() => setConfirmRemove(false)} disabled={removingCheckin}>
                Cancelar
              </button>
            </span>
          )}
          <button
            className="btn btn-sm btn-secondary"
            onClick={handleNewSession}
            disabled={!!votingSub}
            title={votingSub
              ? 'Encerre ou cancele a votação aberta antes'
              : `Normalmente não é necessário: quem fecha a página ou vai embora some sozinho após ${windowFromServer} min. Use só se quiser zerar tudo de uma vez (todos que estão na página voltam a contar).${election.sessionStartedAt ? ` Última vez: ${new Date(election.sessionStartedAt).toLocaleString('pt-BR')}.` : ''}`}
          >
            <FontAwesomeIcon icon={faCalendarDay} /> Nova sessão (novo dia)
          </button>
          <button
            className="btn btn-sm btn-secondary"
            onClick={() => setShowDupHelp((v) => !v)}
            title="Em quais situações uma pessoa pode contar duas vezes"
          >
            <FontAwesomeIcon icon={faCircleQuestion} /> Contagem dupla?
          </button>
          <button className="btn btn-sm btn-secondary" onClick={() => navigate(`/admin/votacoes/${id}/relatorio`)} title="Ver o relatório final com todos os eleitos">
            <FontAwesomeIcon icon={faFileLines} /> Relatório
          </button>
          {featuredSub && !votingSub && (
            <button
              className="btn btn-sm btn-secondary"
              onClick={actions.clearFeature}
              title={`Tirar "${featuredSub.name}" do telão e voltar para a tela de espera com o QR code`}
            >
              <FontAwesomeIcon icon={faEyeSlash} /> Limpar telão
            </button>
          )}
          <a className="btn btn-sm btn-primary" href={telaoUrl} target="_blank" rel="noreferrer" title="Abrir o telão em nova janela para projetar">
            <FontAwesomeIcon icon={faUpRightFromSquare} /> Abrir telão
          </a>
        </div>
      </div>

      <div className="ed-body">
        <div className="ed-left">
          <div className="ed-left-title"><FontAwesomeIcon icon={faChurch} /> Ministérios</div>
          <div className="ed-ministry-list">
            {election.ministries.map((m) => (
              <button
                key={m._id}
                className={`ed-ministry-item ${selectedMid === m._id ? 'active' : ''} ${m.parentMinistryId ? 'is-derived' : ''}`}
                onClick={() => setSelectedMid(m._id)}
              >
                <span className="ed-ministry-name">
                  {m.parentMinistryId && <span className="ed-ministry-derived-mark" title="Escolhido entre os eleitos do ministério acima">↳</span>}
                  {m.name}
                  {m.seats > 1 && <span className="ed-ministry-seats">{m.seats} vagas</span>}
                  {(featuredMid === m._id || m.status === 'voting') && (
                    <span className="ed-ministry-ontv" title="Este ministério está sendo exibido no telão">
                      <FontAwesomeIcon icon={faTv} />
                    </span>
                  )}
                </span>
                <span className={`ed-ministry-badge b-${m.status}`}>
                  {m.status === 'decided' && <><FontAwesomeIcon icon={faCircleCheck} /> Eleito</>}
                  {m.status === 'voting' && <><FontAwesomeIcon icon={faPlay} /> Votando</>}
                  {m.status === 'confirming' && 'Confirmando'}
                  {m.status === 'skipped' && 'Pulado'}
                  {m.status === 'pending' && (m.seats > 1 && (m.electedList || []).length > 0 ? `${m.seats - m.electedList.length} vaga(s)` : 'Pendente')}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="ed-right">
          <div className="ed-preview">
            <div className="ed-preview-label">
              <FontAwesomeIcon icon={faCircleInfo} /> Prévia do telão — é isto que a igreja vê projetado
              {(votingSub || featuredSub) && (
                <span className="ed-preview-ontv">
                  <FontAwesomeIcon icon={faTv} /> {(votingSub || featuredSub).name}
                </span>
              )}
              <span
                className="info-hint info-hint-below"
                data-tip="Use 'Mostrar no telão' no ministério para projetar os nomes durante a discussão. Ao abrir a votação, encerrar ou definir o eleito, o telão acompanha automaticamente."
              >
                <FontAwesomeIcon icon={faCircleInfo} />
              </span>
            </div>
            <TelaoView state={previewState} lastResult={lastResult} qrUrl={voteUrl} compact />
          </div>

          {selected ? (
            <MinistryPanel
              key={selected._id}
              election={election}
              ministry={selected}
              liveVotes={liveVotes}
              checkins={checkins}
              actions={actions}
              isFeatured={featuredMid === selected._id}
              featuredView={publicState?.featured?.view || null}
              anotherVoting={!!votingSub && votingSub._id !== selected._id}
              parentMinistry={selected.parentMinistryId
                ? election.ministries.find(m => m._id === selected.parentMinistryId) || null
                : null}
              derivedChildren={election.ministries.filter(m => m.parentMinistryId === selected._id)}
            />
          ) : (
            <div className="ed-empty-detail">
              <FontAwesomeIcon icon={faCircleInfo} />
              <p>Selecione um ministério à esquerda para cadastrar candidatos e conduzir a votação.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ElectionDashboard;
